import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { InviteMemberInput, UpdateMemberRoleInput } from '@eriko/validation';
import { getMembers, getInvitations, inviteMember, updateMemberRole, removeMember } from '../api/org';
import i18n from '../i18n';

export function useMembers() {
  return useQuery({
    queryKey: ['org', 'members'],
    queryFn: getMembers,
  });
}

export function useInvitations() {
  return useQuery({
    queryKey: ['org', 'invitations'],
    queryFn: getInvitations,
  });
}

export function useInviteMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: InviteMemberInput) => inviteMember(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', 'invitations'] });
      toast.success(i18n.t('team.toastInvited', { ns: 'settings' }));
    },
    onError: (err: Error & { code?: string }) => {
      if (err.code === 'ALREADY_MEMBER') {
        toast.error(i18n.t('team.toastAlreadyMember', { ns: 'settings' }));
      } else {
        toast.error(err.message || i18n.t('team.toastInviteError', { ns: 'settings' }));
      }
    },
  });
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateMemberRoleInput & { id: string }) =>
      updateMemberRole(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', 'members'] });
      toast.success(i18n.t('team.toastRoleUpdated', { ns: 'settings' }));
    },
    onError: (err: Error) => {
      toast.error(err.message || i18n.t('team.toastRoleError', { ns: 'settings' }));
    },
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => removeMember(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', 'members'] });
      toast.success(i18n.t('team.toastRemoved', { ns: 'settings' }));
    },
    onError: (err: Error) => {
      toast.error(err.message || i18n.t('team.toastRemoveError', { ns: 'settings' }));
    },
  });
}
